import React, { useEffect, Fragment, useState, useRef } from 'react';
import { View, Platform, StatusBar } from 'react-native';

import { ViewPropTypes, TextPropTypes } from 'deprecated-react-native-prop-types';
import { isFunction as _isFunction } from 'lodash';
import PropTypes from 'prop-types';

import {
  withTheme,
  getThemeColors,
  LightThemeColors,
} from '@vfgroup-oneplatform/foundation/Components/Themes';
import { usePrevious } from '@vfgroup-oneplatform/foundation/Utils/Hooks';

import FrameworkTestKeys from '../../FrameworkTestKeys';

import {
  useAnimationManager,
  useModalAnimation,
  useTabsAnimation,
  useTrayAnimation,
} from './AnimationManager';
import { TrayModal, BottomBar, Overlay, CollapseIcon, TrayLottieContainer } from './Components';
import styles from './Tray.Styles';
import { TRAY_TYPES } from './Utils/TrayTypesConstants';
import { defaultNormalTrayHeight } from './withTray/withTrayHelper';

function onAnimationFinish(isVisible, finished, setIsVisible) {
  if (!isVisible && finished) {
    setIsVisible(isVisible);
  }
}

const Tray = props => {
  const {
    trayIcon,
    tabStyles,
    customTrayIconStyle,
    isModalVisible,
    activeSubTray,
    toggleModal,
    trayItems,
    images,
    onPressTobi,
    extraStyles,
    trayPlaceHolders,
    dropTray,
    upTray,
    setDropTray,
    showModal,
    hideModal,
    trayType,
    welcomeMessage,
    welcomeMessageBreakTime,
    notificationsBreakTime,
    notifications,
    animationManager,
    theme,
    lottieViewHeight,
    shouldTrayExpand,
    shouldTrayCollapse,
    setTrayHeight,
    expandedTrayHeight,
    normalTrayHeight,
  } = props;
  const previousActiveSubTray = usePrevious(activeSubTray);
  const previousShouldTrayExpand = usePrevious(shouldTrayExpand);
  const previousShouldTrayCollapse = usePrevious(shouldTrayCollapse);

  const timeout = useRef(undefined);
  const _animationManager = useAnimationManager({ animationManager });

  const [_activeSubTray, setActiveSubTray] = useState(activeSubTray);
  // add internal state to do unmounting animations
  const [_isModalVisible, setIsModalVisible] = useState(isModalVisible);

  const [tobiViewHeight, setTobiViewHeight] = useState(0);

  const subTrayTitle = trayItems[_activeSubTray] && trayItems[_activeSubTray].subTrayTitle;
  const content = trayItems[_activeSubTray] && trayItems[_activeSubTray].content;
  const subTrayID = trayItems[_activeSubTray] && trayItems[_activeSubTray].subTrayID;

  const { colors } = getThemeColors(theme.name);

  const { modalAnimation, overlayAnimation, collapseIconAnimation, heightAnimation } =
    useModalAnimation(
      isModalVisible,
      {
        lottieViewHeight,
        tobiViewHeight,
        onAnimationFinish: (isVisible, finished) =>
          onAnimationFinish(isVisible, finished, setIsModalVisible),
      },
      {
        animationManager: _animationManager.current,
      },
    );

  const { tabsAnimation } = useTabsAnimation(
    activeSubTray,
    {
      colors,
      numberOfTabs: trayItems.length,
    },
    {
      animationManager: _animationManager.current,
    },
  );

  const { lottieViewAnimation, bottomBarAnimation } = useTrayAnimation(upTray, dropTray, {
    animationManager: _animationManager.current,
  });

  useEffect(() => {
    if (isModalVisible) {
      setIsModalVisible(isModalVisible);
    }
  }, [isModalVisible]);

  useEffect(() => {
    if (previousShouldTrayExpand === false && shouldTrayExpand === true) {
      _isFunction(setTrayHeight) && setTrayHeight(expandedTrayHeight);
    }
  }, [shouldTrayExpand]);

  useEffect(() => {
    if (previousShouldTrayCollapse === false && shouldTrayCollapse === true) {
      _isFunction(setTrayHeight) && setTrayHeight(normalTrayHeight);
    }
  }, [shouldTrayCollapse]);

  useEffect(() => {
    _isFunction(setTrayHeight) && setTrayHeight(normalTrayHeight);
  }, [normalTrayHeight]);

  useEffect(() => {
    if (
      previousActiveSubTray !== undefined &&
      previousActiveSubTray !== -1 &&
      activeSubTray === -1
    ) {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }

      // same duration of the Tray lottie animation
      const timeoutDuration = isModalVisible ? 333 : 150;

      timeout.current = setTimeout(() => {
        setActiveSubTray(activeSubTray);
        timeout.current = undefined;
      }, timeoutDuration);
    } else if (activeSubTray !== previousActiveSubTray) {
      if (previousActiveSubTray === -1 && timeout.current) {
        clearTimeout(timeout.current);
      }

      setActiveSubTray(activeSubTray);
    }
  }, [activeSubTray, previousActiveSubTray]);

  useEffect(() => {
    const { onRefresh, refreshInterval } = props;
    let onRefreshInterval;
    if (_isFunction(onRefresh) && refreshInterval) {
      onRefreshInterval = setInterval(onRefresh, refreshInterval);
    }
    return () => {
      clearInterval(onRefreshInterval);
    };
  }, []);

  return (
    <Fragment>
      {isModalVisible && <StatusBar translucent backgroundColor="transparent" />}
      {Platform.OS !== 'web' && <CollapseIcon isVisible={_isModalVisible} animation={collapseIconAnimation} />}

      <Overlay isVisible={_isModalVisible} animation={overlayAnimation} onPress={toggleModal} />

      <TrayLottieContainer
        isVisible={isModalVisible}
        animation={lottieViewAnimation}
        height={lottieViewHeight}
        heightAnimation={heightAnimation}
      />

      <TrayModal
        lottieViewHeight={lottieViewHeight}
        toggleModal={toggleModal}
        title={subTrayTitle}
        content={content}
        isVisible={_isModalVisible}
        trayPlaceHolders={trayPlaceHolders}
        accessibilityID={subTrayID}
        animation={modalAnimation}
      />

      <BottomBar
        tabsAnimation={tabsAnimation}
        isVisible={isModalVisible}
        trayItems={trayItems}
        activeSubTray={activeSubTray}
        images={images}
        extraStyles={extraStyles}
        onPressTobi={onPressTobi}
        trayPlaceHolders={trayPlaceHolders}
        trayIcon={trayIcon}
        customTrayIconStyle={customTrayIconStyle}
        dropTray={dropTray}
        upTray={upTray}
        showModal={showModal}
        hideModal={hideModal}
        setDropTray={setDropTray}
        trayType={trayType}
        welcomeMessage={welcomeMessage}
        welcomeMessageBreakTime={welcomeMessageBreakTime}
        notificationsBreakTime={notificationsBreakTime}
        notifications={notifications}
        positionAnimation={bottomBarAnimation}
        getTobiViewHeight={setTobiViewHeight}
        tabStyles={tabStyles}
      />
      {/*  On some android device there is an empty space (separation between the tray and bottom view that is in the TrayLottieContainer), this empty views are to fix this */}
      {Platform.OS === 'android' && trayType == TRAY_TYPES.MAIN ? (
        <>
          <View
            style={styles.leftBackgroundColor(theme)}
            testID={FrameworkTestKeys.trayLeftBackgroundView}
          />
          <View
            style={styles.rightBackgroundColor(theme)}
            testID={FrameworkTestKeys.trayRightBackgroundView}
          />
        </>
      ) : null}
    </Fragment>
  );
};

Tray.defaultProps = {
  trayItems: [],
  isModalVisible: false,
  toggleModal: () => { },
  activeSubTray: -1,
  images: {},
  theme: LightThemeColors,
  upTray: true,
  lottieViewHeight: defaultNormalTrayHeight,
};

Tray.propTypes = {
  trayIcon: PropTypes.element,
  tabStyles: PropTypes.shape({
    title: TextPropTypes.style,
    icon: ViewPropTypes.style,
  }),
  customTrayIconStyle: ViewPropTypes.style,
  onRefresh: PropTypes.func,
  onPressTobi: PropTypes.func,
  refreshInterval: PropTypes.number,
  dropTray: PropTypes.bool,
  upTray: PropTypes.bool,
  setDropTray: PropTypes.func,
  trayItems: PropTypes.array.isRequired,
  isModalVisible: PropTypes.bool.isRequired,
  toggleModal: PropTypes.func.isRequired,
  activeSubTray: PropTypes.number.isRequired,
  images: PropTypes.object,
  extraStyles: ViewPropTypes.style,
  showModal: PropTypes.func.isRequired,
  hideModal: PropTypes.func.isRequired,
  theme: PropTypes.shape({
    name: PropTypes.string,
    isDark: PropTypes.bool,
    colors: PropTypes.object,
  }),
  trayPlaceHolders: PropTypes.object,
  welcomeMessage: PropTypes.string,
  welcomeMessageBreakTime: PropTypes.number,
  notificationsBreakTime: PropTypes.number,
  notifications: PropTypes.arrayOf(Object),
  trayType: PropTypes.string,
  animationManager: PropTypes.object,
  lottieViewHeight: PropTypes.number,
  shouldTrayExpand: PropTypes.bool,
  shouldTrayCollapse: PropTypes.bool,
  setTrayHeight: PropTypes.func,
  expandedTrayHeight: PropTypes.number,
  normalTrayHeight: PropTypes.number,
};

export default withTheme(Tray);
